import React from "react";
import { History, Location } from "history";
import { match } from "react-router-dom";
import TagsTables from "@/components/react/TagsTables";
import { FormattedMessage } from "react-intl";

import {
    mapDataToCategories,
    mapDataToCustomTags,
    Columns,
    onChangeUpdateTags,
    type TagsConfig,
    type TagUpdateValue,
} from "@/components/react/ReactTableTags";
import EntityEditTemplate from "../../../shared/EntityEditTemplate";
import { MATERIAL_TAGS } from "@/lib/ManufacturingTagsEditor.generated";
import { MATERIAL_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";
import { getBackend as getBackend2 } from "@/lib/backend/Backend2";
import { translate } from "@/components/IntlProviderWrapper";
import type { IUpdateMaterialReq } from "@/lib/backend/manufacturing2.generated.types.ts";
import type { ITags } from "@/lib/Models";

import ErrorComponent from "@/components/ErrorComponent";

import UserDocumentationLink from "@/components/UserDocumentationLink";

type Props = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
};

type State = {
    tags: ITags;
    title: string;
    error: string | null;
};

class MaterialEdit extends React.Component<Props, State> {
    state = {
        error: null,
        tags: {},
        title: "",
    };

    componentDidMount() {
        this.load();
    }

    getMaterialUuid = () => {
        return this.props.match.params.uuid;
    };

    load = async () => {
        try {
            this.setState({ error: null });
            const materials = await getBackend2().manufacturing.searchMaterials({
                material_uuid: this.getMaterialUuid(),
            });

            if (materials.data.length > 1) throw Error();

            const material_data = materials.data[0];

            this.setState({
                title: material_data.title,
                tags: material_data.tags,
            });
        } catch (e) {
            this.setState({
                error: translate("common.error", "An error occured while fetching data."),
            });
        }
    };

    onChangeCell = (name: string) => (value: TagUpdateValue) => {
        this.setState({ tags: onChangeUpdateTags(this.state.tags, name, value) });
    };

    renderError = () => {
        if (this.state.error) {
            return <ErrorComponent msg={this.state.error} type="error" />;
        }
        return null;
    };

    onBack = () => {
        this.props.history.goBack();
    };

    onSave = async () => {
        try {
            const uuid = this.getMaterialUuid();

            if (uuid) {
                let material_params: IUpdateMaterialReq = {
                    uuid,
                    tags: this.state.tags,
                };

                await getBackend2().manufacturing.updateMaterial(material_params);
                this.props.history.goBack();
            }
        } catch (e) {
            console.log(e);
            this.setState({
                error: translate("common.error_save", "An error occured while saving data."),
            });
        }
    };

    render() {
        const tagCategories = mapDataToCategories(this.state.tags, MATERIAL_TAGS as TagsConfig, MATERIAL_TAGS_ACCESS);
        const data_nonstd = mapDataToCustomTags(this.state.tags, MATERIAL_TAGS as TagsConfig);
        return (
            <EntityEditTemplate onSave={this.onSave} title={this.state.title} onBack={this.onBack}>
                {this.renderError()}
                <h2 className="mb-4">
                    <FormattedMessage id="common.standard_data" defaultMessage="Standard data" />
                </h2>
                <div className="text-right mb-3">
                    <UserDocumentationLink link_key={"materials"} />
                </div>
                <TagsTables
                    categories={tagCategories}
                    columns={Columns}
                    onChangeCell={this.onChangeCell}
                    customTags={data_nonstd}
                />
            </EntityEditTemplate>
        );
    }
}

export default MaterialEdit;
